import React, { useState, useEffect, useCallback } from 'react';
import tachesApi from '../services/taches';
import './TachesSensibles.css';

const PRIORITES = [
  { id: 'critique', label: 'Critique', color: '#ef4444', bg: '#fef2f2' },
  { id: 'haute', label: 'Haute', color: '#f59e0b', bg: '#fffbeb' },
  { id: 'normale', label: 'Normale', color: '#3b82f6', bg: '#eff6ff' },
  { id: 'basse', label: 'Basse', color: '#9ca3af', bg: '#f9fafb' },
];

const STATUTS = [
  { id: 'a_faire', label: 'A faire', color: '#9ca3af' },
  { id: 'en_cours', label: 'En cours', color: '#3b82f6' },
  { id: 'fait', label: 'Fait', color: '#10b981' },
];

const SENSIBILITES = [
  { id: 'standard', label: 'Standard', color: null },
  { id: 'sensible', label: 'Sensible', color: '#f59e0b' },
  { id: 'tres_sensible', label: 'Tres sensible', color: '#ef4444' },
];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function isOverdue(echeance, statut) {
  if (!echeance || statut === 'fait') return false;
  return new Date(echeance) < new Date(new Date().toISOString().substring(0, 10));
}

function daysUntil(echeance) {
  if (!echeance) return null;
  const diff = Math.ceil((new Date(echeance) - new Date(new Date().toISOString().substring(0, 10))) / 86400000);
  return diff;
}

export default function TachesSensibles({ user, isAdmin }) {
  const [taches, setTaches] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Filtres
  const [filterStatut, setFilterStatut] = useState('');
  const [filterPriorite, setFilterPriorite] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [search, setSearch] = useState('');

  // Édition
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newTache, setNewTache] = useState({ titre: '', description: '', echeance: '', priorite: 'normale', responsable: '', categorie: '', sensibilite: 'standard' });

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterPriorite) params.priorite = filterPriorite;
      if (filterCategorie) params.categorie = filterCategorie;
      const [t, s, c] = await Promise.all([tachesApi.getAll(params), tachesApi.getStats(), tachesApi.getCategories()]);
      setTaches(t.data);
      setStats(s.data);
      setCategories(c.data);
    } catch { showToast('Erreur chargement', 'error'); }
    setLoading(false);
  }, [filterStatut, filterPriorite, filterCategorie, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!newTache.titre.trim()) return;
    try {
      await tachesApi.create(newTache);
      setShowAdd(false);
      setNewTache({ titre: '', description: '', echeance: '', priorite: 'normale', responsable: '', categorie: '', sensibilite: 'standard' });
      showToast('Tache ajoutee');
      await loadData();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    try {
      await tachesApi.update(editingId, editVal);
      setEditingId(null);
      await loadData();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try {
      await tachesApi.remove(id);
      setEditingId(null);
      showToast('Tache supprimee');
      await loadData();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleQuickStatus = async (id, newStatut) => {
    try {
      await tachesApi.update(id, { statut: newStatut });
      await loadData();
    } catch { showToast('Erreur', 'error'); }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditVal({
      titre: t.titre, description: t.description || '', echeance: t.echeance?.substring(0, 10) || '',
      priorite: t.priorite, statut: t.statut, responsable: t.responsable || '', categorie: t.categorie || '',
      sensibilite: t.sensibilite || 'standard',
    });
  };

  // Filtre recherche
  const filtered = taches.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.titre || '').toLowerCase().includes(q) || (t.responsable || '').toLowerCase().includes(q) || (t.categorie || '').toLowerCase().includes(q);
  });

  const prioInfo = (p) => PRIORITES.find(x => x.id === p) || PRIORITES[2];
  const statutInfo = (s) => STATUTS.find(x => x.id === s) || STATUTS[0];

  return (
    <div className="ts-container">
      {toast && <div className={`gantt-toast ${toast.type}`}>{toast.msg}</div>}

      {/* Stats */}
      {stats && (
        <div className="ts-stats">
          <div className="ts-stat"><span className="ts-stat-value">{stats.a_faire}</span><span className="ts-stat-label">A faire</span></div>
          <div className="ts-stat"><span className="ts-stat-value" style={{ color: '#3b82f6' }}>{stats.en_cours}</span><span className="ts-stat-label">En cours</span></div>
          <div className="ts-stat"><span className="ts-stat-value" style={{ color: '#10b981' }}>{stats.fait}</span><span className="ts-stat-label">Fait</span></div>
          {parseInt(stats.en_retard) > 0 && (
            <div className="ts-stat ts-stat-alert"><span className="ts-stat-value" style={{ color: '#ef4444' }}>{stats.en_retard}</span><span className="ts-stat-label">En retard</span></div>
          )}
          {parseInt(stats.critiques) > 0 && (
            <div className="ts-stat ts-stat-alert"><span className="ts-stat-value" style={{ color: '#ef4444' }}>{stats.critiques}</span><span className="ts-stat-label">Critiques</span></div>
          )}
        </div>
      )}

      {/* Filtres */}
      <div className="ts-filters">
        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
          <option value="">Tous statuts</option>
          {STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={filterPriorite} onChange={e => setFilterPriorite(e.target.value)}>
          <option value="">Toutes priorites</option>
          {PRIORITES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {categories.length > 0 && (
          <select value={filterCategorie} onChange={e => setFilterCategorie(e.target.value)}>
            <option value="">Toutes categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="ts-search" />
        {isAdmin && <button className="ts-btn-add" onClick={() => setShowAdd(true)}>+ Nouvelle tache</button>}
      </div>

      {/* Formulaire ajout */}
      {showAdd && (
        <div className="ts-add-form">
          <input value={newTache.titre} onChange={e => setNewTache(v => ({ ...v, titre: e.target.value }))} placeholder="Titre de la tache" autoFocus />
          <input value={newTache.responsable} onChange={e => setNewTache(v => ({ ...v, responsable: e.target.value }))} placeholder="Responsable" />
          <input value={newTache.categorie} onChange={e => setNewTache(v => ({ ...v, categorie: e.target.value }))} placeholder="Categorie (ex: Variables, Vehicules...)" />
          <input type="date" value={newTache.echeance} onChange={e => setNewTache(v => ({ ...v, echeance: e.target.value }))} />
          <select value={newTache.priorite} onChange={e => setNewTache(v => ({ ...v, priorite: e.target.value }))}>
            {PRIORITES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select value={newTache.sensibilite} onChange={e => setNewTache(v => ({ ...v, sensibilite: e.target.value }))}>
            {SENSIBILITES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <textarea value={newTache.description} onChange={e => setNewTache(v => ({ ...v, description: e.target.value }))} placeholder="Description (optionnel)" rows={2} />
          <div className="ts-add-actions">
            <button className="ts-btn-save" onClick={handleAdd}>Ajouter</button>
            <button className="ts-btn-cancel" onClick={() => setShowAdd(false)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Tableau */}
      {loading ? (
        <div className="ts-loading">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="ts-empty">Aucune tache{filterStatut || filterPriorite ? ' avec ces filtres' : ''}.</div>
      ) : (
        <div className="ts-table-container">
          <table className="ts-table">
            <thead>
              <tr>
                <th style={{ width: '36px' }}></th>
                <th>Tache</th>
                <th>Categorie</th>
                <th>Responsable</th>
                <th>Echeance</th>
                <th>Priorite</th>
                <th>Statut</th>
                {isAdmin && <th style={{ width: '60px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const overdue = isOverdue(t.echeance, t.statut);
                const days = daysUntil(t.echeance);
                const prio = prioInfo(t.priorite);
                const stat = statutInfo(t.statut);

                if (editingId === t.id) {
                  return (
                    <tr key={t.id} className="ts-row-editing">
                      <td>
                        <select value={editVal.sensibilite} onChange={e => setEditVal(v => ({ ...v, sensibilite: e.target.value }))}>
                          {SENSIBILITES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <input value={editVal.titre} onChange={e => setEditVal(v => ({ ...v, titre: e.target.value }))} />
                        <textarea value={editVal.description} onChange={e => setEditVal(v => ({ ...v, description: e.target.value }))} rows={2} placeholder="Description" />
                      </td>
                      <td><input value={editVal.categorie} onChange={e => setEditVal(v => ({ ...v, categorie: e.target.value }))} /></td>
                      <td><input value={editVal.responsable} onChange={e => setEditVal(v => ({ ...v, responsable: e.target.value }))} /></td>
                      <td><input type="date" value={editVal.echeance} onChange={e => setEditVal(v => ({ ...v, echeance: e.target.value }))} /></td>
                      <td>
                        <select value={editVal.priorite} onChange={e => setEditVal(v => ({ ...v, priorite: e.target.value }))}>
                          {PRIORITES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={editVal.statut} onChange={e => setEditVal(v => ({ ...v, statut: e.target.value }))}>
                          {STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="ts-actions">
                        <button className="ts-action-btn save" onClick={handleSaveEdit}>✓</button>
                        <button className="ts-action-btn cancel" onClick={() => setEditingId(null)}>✕</button>
                        <button className="ts-action-btn danger" onClick={() => handleDelete(t.id)}>&#x1F5D1;</button>
                      </td>
                    </tr>
                  );
                }

                const sensi = SENSIBILITES.find(x => x.id === t.sensibilite);
                const sensiColor = sensi?.color;

                return (
                  <tr key={t.id} className={`ts-row ${t.statut === 'fait' ? 'ts-row-done' : ''} ${overdue ? 'ts-row-overdue' : ''}`}
                    style={sensiColor ? { borderLeft: `4px solid ${sensiColor}` } : undefined}>
                    <td title={sensi?.label || ''}>
                      {isAdmin && t.statut !== 'fait' && (
                        <button className="ts-check" onClick={() => handleQuickStatus(t.id, 'fait')} title="Marquer fait">○</button>
                      )}
                      {t.statut === 'fait' && <span className="ts-check-done">✓</span>}
                    </td>
                    <td className="ts-cell-titre">
                      <span className="ts-titre">{t.titre}</span>
                      {t.description && <span className="ts-desc">{t.description}</span>}
                    </td>
                    <td>{t.categorie ? <span className="ts-cat">{t.categorie}</span> : '-'}</td>
                    <td>{t.responsable || '-'}</td>
                    <td className={overdue ? 'ts-overdue-date' : ''}>
                      {t.echeance ? formatDate(t.echeance) : '-'}
                      {days !== null && t.statut !== 'fait' && (
                        <span className={`ts-days ${days < 0 ? 'late' : days <= 3 ? 'soon' : ''}`}>
                          {days < 0 ? `${Math.abs(days)}j retard` : days === 0 ? "Aujourd'hui" : `${days}j`}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="ts-prio-badge" style={{ background: prio.bg, color: prio.color, borderColor: prio.color }}>
                        {prio.label}
                      </span>
                    </td>
                    <td>
                      {isAdmin ? (
                        <select className="ts-statut-select" value={t.statut}
                          style={{ color: stat.color }}
                          onChange={e => handleQuickStatus(t.id, e.target.value)}>
                          {STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      ) : (
                        <span className="ts-statut-badge" style={{ color: stat.color }}>{stat.label}</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="ts-actions">
                        <button className="ts-action-btn edit" onClick={() => startEdit(t)}>✎</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
